import { getMonth, getYear, isAfter } from "date-fns";
import { formatDateEndOfDay } from "helpers/formatDate";

import api from "./api";

import { StatusInscricaoMap, TypeStatusInscricao } from "interfaces/inscricoes";
import {
  IRelPaymentsPeriod,
  IRelPaymentsPeriodTotals,
  IReports,
  RelatorioPaymentsTipoData,
} from "../interfaces/reports";

// LISTAR TODOS OS ATLETAS DO EVENTOS
export async function getAllAtletaEvento(eventoId: number) {
  try {
    const { data, status } = await api.get(`/rel/atletainscrito/${eventoId}`);

    return { data, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação com o servidor");
  }
}

export async function getReportInscricao(
  eventoId: number,
  statusInsc?: TypeStatusInscricao
): Promise<{ data: IReports[]; status: number }> {
  try {
    const { data, status } = await api.post<IReports[]>("/rel/inscricoes", {
      eventoId,
      ...(statusInsc && { statusInscId: StatusInscricaoMap[statusInsc] }),
    });
    return { data, status };
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação com o servidor");
  }
}

export async function getPaymentsPeriod(
  startDate: string | Date,
  endDate: string | Date,
  tipoData: RelatorioPaymentsTipoData = "pagamento"
): Promise<{
  totals: Partial<{
    [k in TypeStatusInscricao]: { qtd: number; total: number };
  }>;
  data: IRelPaymentsPeriod[];
  status: number;
}> {
  try {
    const { data: response, status } = await api.post<{
      data: IRelPaymentsPeriod[];
      totals: IRelPaymentsPeriodTotals;
    }>("/rel/payments", {
      startDate,
      endDate,
      tipoData,
    });

    const rows = Array.isArray(response) ? response : (response?.data ?? []);
    const totals = Array.isArray(response)
      ? undefined
      : response?.totals;

    return {
      totals: totals ?? {
        Pago: { qtd: 0, total: 0 },
        Pendente: { qtd: 0, total: 0 },
        Cancelado: { qtd: 0, total: 0 },
      },
      data: rows,
      status,
    };
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação com o servidor");
  }
}

interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    color:
      | "primary"
      | "secondary"
      | "info"
      | "success"
      | "warning"
      | "error"
      | "light"
      | "dark";
    data: number[];
  }[];
}

export async function generateByYearReport(
  startDate: string | Date = `${new Date().getFullYear() - 1}-01-01`,
  endDate: string | Date = `${new Date().getFullYear()}-12-31`
) {
  const dataGraphic: ChartData = {
    labels: [
      "Jan",
      "Fev",
      "Mar",
      "Abr",
      "Mai",
      "Jun",
      "Jul",
      "Ago",
      "Set",
      "Out",
      "Nov",
      "Dez",
    ],
    datasets: [],
  };

  const start = formatDateEndOfDay(startDate, "yyyy-MM-dd HH:mm:ss");
  const end = formatDateEndOfDay(endDate, "yyyy-MM-dd HH:mm:ss");

  // Validação simples para garantir que startDate não seja maior que endDate
  if (isAfter(start, end)) {
    return {
      message: "Data inicial deve ser menor que a data final",
      status: 400,
    };
  }

  // Chama a API para gerar o relatório
  try {
    const { data: response, status } = await api.post<{
      data: IRelPaymentsPeriod[];
      totals: IRelPaymentsPeriodTotals;
    }>("/rel/payments", {
      startDate: start,
      endDate: end,
    });
    if (status !== 200) return { data: dataGraphic, status };

    const data = Array.isArray(response) ? response : (response?.data ?? []);

    // Filtrar os dados do primeiro ano
    const yearStart = data.filter(
      (item: IRelPaymentsPeriod) =>
        getYear(formatDateEndOfDay(item.dataBaixa, "yyyy-MM-dd HH:mm:ss")) ===
        getYear(start)
    );

    // Filtrar os dados do segundo ano
    const yearEnd = data.filter(
      (item: IRelPaymentsPeriod) =>
        getYear(formatDateEndOfDay(item.dataBaixa, "yyyy-MM-dd HH:mm:ss")) ===
        getYear(end)
    );

    const emptyMonthsAcc = {
      1: { month: "Janeiro", qtd: 0, total: 0 },
      2: { month: "Fevereiro", qtd: 0, total: 0 },
      3: { month: "Março", qtd: 0, total: 0 },
      4: { month: "Abril", qtd: 0, total: 0 },
      5: { month: "Maio", qtd: 0, total: 0 },
      6: { month: "Junho", qtd: 0, total: 0 },
      7: { month: "Julho", qtd: 0, total: 0 },
      8: { month: "Agosto", qtd: 0, total: 0 },
      9: { month: "Setembro", qtd: 0, total: 0 },
      10: { month: "Outubro", qtd: 0, total: 0 },
      11: { month: "Novembro", qtd: 0, total: 0 },
      12: { month: "Dezembro", qtd: 0, total: 0 },
    };

    // Calcular os totais de recebimentos do primeiro ano
    const resultFishYear = yearStart.reduce(
      (
        acc: { [k: string]: { month: string; qtd: number; total: number } },
        item: IRelPaymentsPeriod
      ) => {
        const monthNumber = extractMonth(
          formatDateEndOfDay(item.dataBaixa, "yyyy-MM-dd HH:mm:ss")
        );

        if (!acc[monthNumber.month])
          acc[monthNumber.month] = { month: "", qtd: 0, total: 0 };

        acc[monthNumber.month] = {
          month: monthNumber.monthName,
          qtd: acc[monthNumber.month].qtd + 1,
          total:
            acc[monthNumber.month].total +
            Number(item.total ?? item.inscricaoEvento.totalInscricao),
        };
        return acc;
      },
      { ...emptyMonthsAcc }
    );

    const resultSecondYear = yearEnd.reduce(
      (
        acc: { [k: string]: { month: string; qtd: number; total: number } },
        item: IRelPaymentsPeriod
      ) => {
        const monthNumber = extractMonth(
          formatDateEndOfDay(item.dataBaixa, "yyyy-MM-dd HH:mm:ss")
        );

        if (!acc[monthNumber.month])
          acc[monthNumber.month] = { month: "", qtd: 0, total: 0 };

        acc[monthNumber.month] = {
          month: monthNumber.monthName,
          qtd: acc[monthNumber.month].qtd + 1,
          total:
            acc[monthNumber.month].total +
            Number(item.total ?? item.inscricaoEvento.totalInscricao),
        };
        return acc;
      },
      { ...emptyMonthsAcc }
    );

    // Adicionar os totais ao gráfico
    dataGraphic.datasets.push({
      label: getYear(start).toString(),
      color: "light",
      data: Object.values(resultFishYear).map((item: any) => item.total),
    });
    dataGraphic.datasets.push({
      label: getYear(end).toString(),
      color: "warning",
      data: Object.values(resultSecondYear).map((item: any) => item.total),
    });

    return { data: dataGraphic, status: 200 };
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação com o servidor");
  }
}

function extractMonth(date: string | Date) {
  const months = [
    "Janeiro",
    "Fevereiro",
    "Março",
    "Abril",
    "Maio",
    "Junho",
    "Julho",
    "Agosto",
    "Setembro",
    "Outubro",
    "Novembro",
    "Dezembro",
  ];
  const month = getMonth(formatDateEndOfDay(date, "yyyy-MM-dd HH:mm:ss"));
  return { month: month + 1, monthName: months[month] };
}

type estatisticaRegisterWeekType = {
  totalAtleta: number;
  firstWeek: number;
  secondWeek: number;
};
export async function getEstatisticaRegisterWeek(): Promise<estatisticaRegisterWeekType> {
  try {
    const result = await api.get<estatisticaRegisterWeekType>(
      "/rel/estatisticas/register-week"
    );
    return result.data;
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação com o servidor");
  }
}

type estatisticaReceitaAnualType = {
  yearCurrent: number;
  lastYear: number;
  periodoLabel: string;
};
export async function getEstatisticaReceitaAnual(): Promise<estatisticaReceitaAnualType> {
  try {
    const result = await api.get<estatisticaReceitaAnualType>(
      "/rel/estatisticas/receita-ano"
    );
    return result.data;
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação com o servidor");
  }
}

export type TotalEquipesType = {
  totalEquipes: number;
  equipes: {
    _count: {
      equipe: number;
    };
    equipe: string | null;
  }[];
};
export async function getTotalEquipes(): Promise<TotalEquipesType> {
  try {
    const result = await api.get<TotalEquipesType>(
      "/rel/estatisticas/total-equipes"
    );
    return result.data;
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação com o servidor");
  }
}

// Total de inscrições por cidade
export type TotalInscricaoCityType = {
  cidade: string;
  qtd: number;
};
export async function getTotalInscricaoPorCity(): Promise<
  TotalInscricaoCityType[]
> {
  try {
    const result = await api.get(
      "/rel/estatisticas/total-inscricao-city?take=200"
    );
    return result.data;
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação com o servidor");
  }
}

// Total de inscrições por equipe
export type TotalInscricaoPorEquipeType = {
  equipe: string;
  qtd: number;
};
export async function getTotalInscricaoPorEquipe(): Promise<
  TotalInscricaoPorEquipeType[]
> {
  try {
    const result = await api.get("/rel/estatisticas/total-inscricao-equipes");
    return result.data;
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação com o servidor");
  }
}

// Total de inscrições por Mês
type TotalInscricaoPorMesType = {
  totalInscricao: number;
  dataInscricoes: string[];
};
export async function getTotalInscricaoPorMes(): Promise<TotalInscricaoPorMesType> {
  try {
    const result = await api.get("/rel/estatisticas/total-inscricao-mes");
    return result.data;
  } catch (error: any) {
    throw new Error(error.message || "Falha na comunicação com o servidor");
  }
}
